import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, History, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS, type RunnableChecklist } from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  Field,
  Input,
  Modal,
  Spinner,
  StatusPill,
  Textarea,
  runStatusTone,
} from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth, useCan } from '../../lib/auth';
import { formatDateTime, humanise } from '../../lib/format';

/** Supervisor verification view (directive §32). */
export function RunReviewPage() {
  const { runId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuth((s) => s.user);
  const canVerify = useCan(PERMISSIONS.RUN_VERIFY);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['run', runId],
    queryFn: () => api.get<RunnableChecklist>(`/api/checklist-runs/${runId}`),
  });

  const verify = useMutation({
    mutationFn: (input: { notes: string; signedName: string }) =>
      api.post(`/api/checklist-runs/${runId}/verify`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries();
      setVerifyOpen(false);
      navigate('/supervisor');
    },
    onError: (caught: Error) => setError(caught.message),
  });

  const reject = useMutation({
    mutationFn: (reason: string) => api.post(`/api/checklist-runs/${runId}/reject`, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
      setRejectOpen(false);
      navigate('/supervisor');
    },
    onError: (caught: Error) => setError(caught.message),
  });

  if (isLoading || !data) return <Spinner label="Loading checklist" />;

  const items = data.sections.flatMap((s) => s.items);
  const answerByItem = new Map(data.answers.map((a) => [a.itemId, a]));
  const abnormal = data.answers.filter((a) => a.result === 'FAIL' || a.result === 'ABNORMAL');
  const problemByItem = new Map(
    data.problems.filter((p) => p.itemId).map((p) => [p.itemId!, p]),
  );

  const awaitingVerification =
    data.run.status === 'SUBMITTED' || data.run.status === 'PARTIALLY_COMPLETED';
  const isOwnRun = data.run.operatorId === user?.id;

  return (
    <div>
      <PageHeader
        title={data.template.name}
        back={{ to: '/checklists', label: 'All checklists' }}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Link to={`/machines/${data.machine.id}`} className="font-semibold hover:underline">
              {data.machine.code}
            </Link>
            <span>
              {data.machine.name} · {data.machine.lineName} · v{data.template.versionNo}
            </span>
            <StatusPill tone={runStatusTone(data.run.status)} label={humanise(data.run.status)} />
          </span>
        }
        actions={
          canVerify && awaitingVerification && !isOwnRun ? (
            <>
              <Button variant="secondary" icon={X} onClick={() => setRejectOpen(true)}>
                Return to operator
              </Button>
              <Button variant="success" icon={Check} onClick={() => setVerifyOpen(true)}>
                Verify
              </Button>
            </>
          ) : null
        }
      />

      <div className="space-y-4 p-4 lg:p-6">
        {error ? <Callout tone="alarm">{error}</Callout> : null}

        {canVerify && awaitingVerification && isOwnRun ? (
          <Callout tone="warn" title="You performed this checklist">
            Someone else has to verify it. Self-verification would make the supervisor check
            meaningless.
          </Callout>
        ) : null}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <SummaryTile label="Tasks completed" value={`${data.run.completedItems} / ${data.run.totalItems}`} />
          <SummaryTile
            label="Abnormalities"
            value={String(abnormal.length)}
            tone={abnormal.length > 0 ? 'warn' : 'ok'}
          />
          <SummaryTile
            label="Problem tags"
            value={String(data.problems.length)}
            tone={data.problems.length > 0 ? 'warn' : 'ok'}
          />
          <SummaryTile label="Submitted" value={formatDateTime(data.run.submittedAt)} />
        </div>

        {data.run.notes ? (
          <Card className="p-4">
            <p className="text-xs font-bold tracking-wide text-steel-500 uppercase">
              Operator notes
            </p>
            <p className="mt-1 text-sm whitespace-pre-line text-steel-800">{data.run.notes}</p>
          </Card>
        ) : null}

        {abnormal.length > 0 ? (
          <Card>
            <CardHeader
              title="Abnormal findings"
              subtitle="Review these before verifying"
            />
            <ul className="divide-y divide-steel-100">
              {abnormal.map((answer) => {
                const item = items.find((i) => i.id === answer.itemId);
                const problem = item ? problemByItem.get(item.id) : undefined;
                if (!item) return null;
                return (
                  <li key={answer.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-steel-900">
                          #{item.seq} {item.task}
                        </p>
                        <p className="text-xs text-steel-500">Standard: {item.standard}</p>
                        <p className="mt-1 text-sm font-semibold text-alarm-700">
                          Recorded: {renderValue(answer, item.unit)}
                        </p>
                        {answer.comment ? (
                          <p className="mt-1 text-sm text-steel-700">“{answer.comment}”</p>
                        ) : null}
                      </div>
                      {problem ? (
                        <Link
                          to={`/problems/${problem.id}`}
                          className="shrink-0 text-xs font-bold text-info-700 hover:underline"
                        >
                          {problem.tagNo} →
                        </Link>
                      ) : (
                        <Badge tone="warn" icon={AlertTriangle}>
                          No tag
                        </Badge>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
        ) : (
          <Callout tone="ok" title="No abnormalities">
            Every task met its standard on this run.
          </Callout>
        )}

        {data.sections.map((section) => (
          <Card key={section.id}>
            <CardHeader title={section.title} subtitle={section.description ?? undefined} />
            <ul className="divide-y divide-steel-100">
              {section.items.map((item) => {
                const answer = answerByItem.get(item.id);
                const bad = answer?.result === 'FAIL' || answer?.result === 'ABNORMAL';
                return (
                  <li key={item.id} className="flex items-start gap-3 px-4 py-2.5">
                    <span className="w-6 shrink-0 text-xs font-bold text-steel-400 tabular-nums">
                      {item.seq}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-steel-900">{item.task}</span>
                      {answer?.comment ? (
                        <span className="block text-xs text-steel-500">“{answer.comment}”</span>
                      ) : null}
                    </span>
                    <span className="shrink-0">
                      {!answer ? (
                        <Badge>Not answered</Badge>
                      ) : (
                        <StatusPill
                          tone={bad ? 'alarm' : answer.result === 'NA' ? 'neutral' : 'ok'}
                          label={
                            answer.valueNumber != null
                              ? `${answer.valueNumber}${item.unit ? ` ${item.unit}` : ''}`
                              : humanise(answer.result)
                          }
                        />
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </Card>
        ))}

        <Link
          to={`/admin/audit?entityType=checklist_run&entityId=${runId}`}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-info-700 hover:underline"
        >
          <History aria-hidden className="size-4" />
          View the full audit trail for this checklist
        </Link>
      </div>

      <VerifyModal
        open={verifyOpen}
        onClose={() => setVerifyOpen(false)}
        defaultName={user?.fullName ?? ''}
        abnormalCount={abnormal.length}
        busy={verify.isPending}
        onSubmit={(notes, signedName) => verify.mutate({ notes, signedName })}
      />

      <RejectModal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        busy={reject.isPending}
        onSubmit={(reason) => reject.mutate(reason)}
      />
    </div>
  );
}

function renderValue(
  answer: { valueNumber?: number | null; valueText?: string | null; valueBool?: boolean | null; result: string },
  unit: string | null,
): string {
  if (answer.valueNumber != null) return `${answer.valueNumber}${unit ? ` ${unit}` : ''}`;
  if (answer.valueText) return answer.valueText;
  if (answer.valueBool === false) return 'FAIL';
  return humanise(answer.result);
}

function SummaryTile({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'ok' | 'warn';
}) {
  return (
    <Card className="p-3">
      <p className="text-xs font-semibold tracking-wide text-steel-500 uppercase">{label}</p>
      <p
        className={
          tone === 'warn'
            ? 'mt-1 text-lg font-bold text-warn-700'
            : tone === 'ok'
              ? 'mt-1 text-lg font-bold text-ok-700'
              : 'mt-1 text-lg font-bold text-steel-900'
        }
      >
        {value}
      </p>
    </Card>
  );
}

function VerifyModal({
  open,
  onClose,
  defaultName,
  abnormalCount,
  busy,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  defaultName: string;
  abnormalCount: number;
  busy: boolean;
  onSubmit: (notes: string, signedName: string) => void;
}) {
  const [notes, setNotes] = useState('');
  const [name, setName] = useState(defaultName);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Verify this checklist"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="success"
            fullWidth
            loading={busy}
            disabled={name.trim().length < 2}
            onClick={() => onSubmit(notes.trim(), name.trim())}
          >
            Verify
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {abnormalCount > 0 ? (
          <Callout tone="warn">
            This run has {abnormalCount} abnormal finding{abnormalCount === 1 ? '' : 's'}.
            Confirm each has a problem tag or a satisfactory explanation before verifying.
          </Callout>
        ) : null}
        <Field label="Notes" hint="Optional — recorded on the run and in the audit trail.">
          <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} />
        </Field>
        <Field label="Your name" required>
          <Input value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function RejectModal({
  open,
  onClose,
  busy,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  busy: boolean;
  onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Return to the operator"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            fullWidth
            loading={busy}
            disabled={reason.trim().length < 3}
            onClick={() => onSubmit(reason.trim())}
          >
            Return it
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Callout tone="info">
          The checklist goes back to the operator as in-progress so they can correct it. Nothing
          they already recorded is deleted.
        </Callout>
        <Field label="What needs correcting?" required>
          <Textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={4}
            placeholder="e.g. Task 9 fill weight looks like a typo — please re-check and re-enter."
          />
        </Field>
      </div>
    </Modal>
  );
}
